import React, { useMemo } from "react";
import {
  Alert,
  Box,
  Chip,
  Divider,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import { NameMatchEvaluateResponse } from "./types";

interface NameMatchResultViewProps {
  result: NameMatchEvaluateResponse;
  /** When true, fail/unmatched reason lines are listed before other reasons. */
  prioritizeFailures?: boolean;
  footnote?: React.ReactNode;
}

function isFailReason(reason: string) {
  return (
    reason.includes("could not")
    || reason.includes("did not")
    || reason.includes("→ REJECT")
  );
}

function reasonIcon(reason: string) {
  if (isFailReason(reason)) return "✗";
  if (reason.startsWith("(Note")) return "i";
  if (
    reason.includes("ACCEPT")
    || reason.includes("REJECT")
    || reason.startsWith("All ")
  ) {
    return "★";
  }
  return "✓";
}

function TokenChips({
  tokens,
  tone,
}: {
  tokens: string[];
  tone: "pan" | "bank";
}) {
  const sx =
    tone === "pan"
      ? { bgcolor: "#e8f4fb", color: "#007CBF" }
      : { bgcolor: "#fff4e0", color: "#c97a00" };

  if (tokens.length === 0) {
    return (
      <Typography variant="caption" color="text.disabled">
        (none)
      </Typography>
    );
  }

  return (
    <Stack direction="row" flexWrap="wrap" gap={0.75}>
      {tokens.map((token, index) => (
        <Chip
          key={`${token}-${index}`}
          label={token}
          size="small"
          sx={{
            fontFamily: "monospace",
            fontWeight: 700,
            fontSize: 12,
            ...sx,
          }}
        />
      ))}
    </Stack>
  );
}

export default function NameMatchResultView({
  result,
  prioritizeFailures = false,
  footnote,
}: NameMatchResultViewProps) {
  const isAccept = result.verdict === "ACCEPT";

  const orderedReasons = useMemo(() => {
    if (!prioritizeFailures) return result.reasons;
    const failures: string[] = [];
    const others: string[] = [];
    result.reasons.forEach((reason) => {
      if (isFailReason(reason)) failures.push(reason);
      else others.push(reason);
    });
    return [...failures, ...others];
  }, [prioritizeFailures, result.reasons]);

  return (
    <>
      <Alert
        severity={isAccept ? "success" : "error"}
        icon={false}
        sx={{
          mb: 2,
          borderRadius: 2,
          border: `1px solid ${isAccept ? "#10b981" : "#ef4444"}`,
          bgcolor: isAccept ? "#edfdf6" : "#fff2f1",
        }}
      >
        <Box display="flex" alignItems="center" gap={2} width="100%">
          <Box flex={1}>
            <Typography
              fontWeight={900}
              fontSize={22}
              letterSpacing={1.5}
              color={isAccept ? "#059669" : "#dc2626"}
            >
              {result.verdict}
            </Typography>
            <Typography variant="body2" color="text.secondary" mt={0.3}>
              {result.matchedCount} of {result.totalTokens} token(s) matched
            </Typography>
            {footnote && (
              <Typography
                variant="caption"
                color="text.secondary"
                display="block"
                mt={0.75}
              >
                {footnote}
              </Typography>
            )}
          </Box>
        </Box>
      </Alert>

      <Paper
        elevation={0}
        sx={{ border: "1px solid #e3e8ef", borderRadius: 2, overflow: "hidden", mb: 2 }}
      >
        <Box sx={{ px: 2.5, py: 1.5, borderBottom: "1px solid #f0f2f5", bgcolor: "#fff" }}>
          <Typography variant="body2" fontWeight={700}>
            Processed tokens
          </Typography>
        </Box>
        <Box
          sx={{ p: 2.5 }}
          display="grid"
          gridTemplateColumns={{ xs: "1fr", sm: "1fr 1fr" }}
          gap={2}
        >
          <Box>
            <Typography
              variant="caption"
              fontWeight={700}
              color="text.disabled"
              sx={{ textTransform: "uppercase", letterSpacing: 0.6, display: "block", mb: 1 }}
            >
              PAN
            </Typography>
            <TokenChips tokens={result.panTokens} tone="pan" />
          </Box>
          <Box>
            <Typography
              variant="caption"
              fontWeight={700}
              color="text.disabled"
              sx={{ textTransform: "uppercase", letterSpacing: 0.6, display: "block", mb: 1 }}
            >
              Bank
            </Typography>
            <TokenChips tokens={result.bankTokens} tone="bank" />
          </Box>
        </Box>
      </Paper>

      <Paper
        elevation={0}
        sx={{ border: "1px solid #e3e8ef", borderRadius: 2, overflow: "hidden" }}
      >
        <Box sx={{ px: 2.5, py: 1.5, borderBottom: "1px solid #f0f2f5" }}>
          <Typography variant="body2" fontWeight={700}>
            Reasoning
          </Typography>
        </Box>
        <Box sx={{ px: 2.5, py: 1 }}>
          <Stack divider={<Divider flexItem />} spacing={0}>
            {orderedReasons.map((reason, index) => {
              const isFail = isFailReason(reason);
              const isVerdict =
                reason.includes("ACCEPT")
                || reason.includes("REJECT")
                || reason.startsWith("All ");
              return (
                <Box key={`${index}-${reason}`} display="flex" gap={1.5} alignItems="flex-start" py={1}>
                  <Typography fontSize={14} sx={{ flexShrink: 0, mt: 0.1 }}>
                    {reasonIcon(reason)}
                  </Typography>
                  <Typography
                    variant="body2"
                    color={isFail ? "error.main" : isVerdict ? "text.primary" : "text.secondary"}
                    fontWeight={isVerdict ? 700 : 400}
                  >
                    {reason}
                  </Typography>
                </Box>
              );
            })}
          </Stack>
        </Box>
      </Paper>
    </>
  );
}
